import React, { useState, useRef, useEffect } from 'react';
import Icon from '../components/common/Icon';
import { HeroBackgroundStrokes } from '../components/common/SyncratikLogo';
import starterGradient from '../assets/gradients/pricing-gradient-starter.png';
import standardGradient from '../assets/gradients/pricing-gradient-standard.png';
import proGradient from '../assets/gradients/pricing-gradient-pro.png';

const PricingPage = ({ openModal }) => {
    const [pricingType, setPricingType] = useState('individual'); // 'individual' or 'enterprise'
    const individualRef = useRef(null);
    const enterpriseRef = useRef(null);
    const [sliderStyle, setSliderStyle] = useState({});

    useEffect(() => {
        const updateSlider = () => {
            const activeButton = pricingType === 'individual' ? individualRef.current : enterpriseRef.current;
            if (activeButton) {
                setSliderStyle({
                    width: `${activeButton.offsetWidth}px`,
                    transform: `translateX(${activeButton.offsetLeft - 4}px)`, // Subtract 4px (0.25rem) to account for container padding
                });
            }
        };

        updateSlider();
        window.addEventListener('resize', updateSlider);
        return () => window.removeEventListener('resize', updateSlider);
    }, [pricingType]);

    const getDashboardUrl = () => {
        const isLocal = window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname === 'local.dashboard.syncratik' ||
            process.env.NODE_ENV === 'development';
        return isLocal ? 'http://localhost:7287' : 'https://dashboard.syncratik.com/';
    };

    const handleChoosePlan = () => {
        const base = getDashboardUrl();
        const target = base.replace(/\/$/, '') + '/billing';
        window.location.href = target;
    };

    // Pricing data based on type
    const pricingData = {
        individual: {
            starter: {
                name: 'Starter',
                description: 'Try for free',
                price: '$0',
                priceSubtext: 'For a 2 week trial',
                features: [
                    'Knowledge base integration',
                    'Customized agent logic',
                    'Wide selection of agent voices',
                    'Automatic updates to the latest models'
                ]
            },
            standard: {
                name: 'Standard',
                description: 'For daily front desk operations',
                price: '$0.45',
                priceSubtext: 'Per minute',
                previousTier: 'Starter',
                features: [
                    'CRM and calendar integration',
                    'SIP trunking and telephony',
                    '24/7 agent call availability',
                    '10 concurrent calls',
                    'Prebuilt data & analytics (Transcripts and sentiment)',
                    'Priority support'
                ]
            },
            pro: {
                name: 'Pro',
                description: 'For extensive customizations',
                price: '$0.50',
                priceSubtext: 'Per minute',
                previousTier: 'Standard',
                features: [
                    'Dedicated server hosting',
                    'Full selection of agent voices',
                    'English + 2 additional languages',
                    '10 more concurrent calls (20 total)',
                    'Custom data & analytics',
                    'AI model training on previous calls',
                    'SOC I, SOC II, and HIPAA compliance'
                ]
            }
        },
        enterprise: {
            starter: {
                name: 'Team',
                description: 'For growing teams',
                price: 'Custom',
                priceSubtext: 'Volume pricing available',
                features: [
                    'Everything in Individual Pro',
                    '50+ concurrent calls',
                    'Dedicated account manager',
                    'Custom SLA guarantees',
                    'Advanced security & compliance',
                    'Multi-region deployment'
                ]
            },
            standard: {
                name: 'Business',
                description: 'For organizations at scale',
                price: 'Custom',
                priceSubtext: 'Contact for pricing',
                previousTier: 'Team',
                features: [
                    'Unlimited concurrent calls',
                    'White-glove onboarding',
                    'Custom integration development',
                    '24/7 dedicated support',
                    'Advanced analytics & reporting',
                    'Custom AI model training'
                ]
            }
        }
    };

    const currentPricing = pricingData[pricingType];
    const tierKeys = pricingType === 'enterprise'
        ? ['starter', 'standard']
        : ['starter', 'standard', 'pro'];
    const gradientImages = [starterGradient, standardGradient, proGradient];

    const renderPricingCard = (tierKey, index) => {
        const tier = currentPricing[tierKey];
        const isStandard = tierKey === 'standard';
        const gradientImage = gradientImages[index];

        return (
            <div
                key={tierKey}
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 bg-gray-95 ${isStandard ? '' : 'shadow-lg hover:shadow-2xl'}`}
                style={isStandard ? { boxShadow: '0 0 14px 0 #B67CE9' } : {}}
            >
                <img
                    src={gradientImage}
                    alt=""
                    className="absolute top-0 left-0 w-full object-cover pointer-events-none"
                    style={{ height: '200px' }}
                />
                <div className="relative h-full flex flex-col" style={{ padding: '2rem 1.75rem 2.25rem' }}>
                    <div>
                        <h2 className="text-h2 text-gray-10">{tier.name}</h2>
                        <p className="text-paragraph text-gray-25 mt-1">{tier.description}</p>
                        <div style={{ marginTop: '0.75rem' }}>
                            <p className="text-h3 text-gray-10">{tier.price}</p>
                            <p className="text-small text-gray-25">{tier.priceSubtext}</p>
                        </div>
                    </div>

                    <button
                        onClick={handleChoosePlan}
                        className={`w-full ${isStandard ? 'btn-primary' : 'btn-primary-outline'}`}
                        style={{ padding: '0.5rem 1rem', marginTop: '1.75rem' }}
                    >
                        Get Started
                    </button>

                    <div className="space-y-3 flex-grow" style={{ marginTop: '1.75rem' }}>
                        {tier.previousTier ? (
                            <p className="text-paragraph font-semibold text-gray-10">
                                Everything in <span className="text-purple-60">{tier.previousTier}</span>, plus:
                            </p>
                        ) : null}
                        <ul className="space-y-3">
                            {tier.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start text-paragraph text-gray-25">
                                    <Icon path="M4.5 12.75l6 6 9-13.5" className="w-5 h-5 text-gray-25 mr-3 mt-0.5 flex-shrink-0" />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-gray-10 min-h-screen relative pt-16 lg:pt-60 pb-60">
            {/* Background with logo strokes - same as contact page */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <HeroBackgroundStrokes
                    className="absolute inset-0 w-full"
                    style={{
                        height: '100%',
                        backgroundImage: 'url(/assets/logo-strokes.png)',
                        backgroundRepeat: 'repeat-y',
                        backgroundSize: '100% 2216px',
                        backgroundPosition: 'center top'
                    }}
                />
            </div>

            <div className="container-content relative z-10">
                {/* Header */}
                <div className="text-center">
                    <h1 className="text-h1 md:text-h0 text-gray-95 mb-4">
                        Flexible pricing for every team
                    </h1>
                    <p className="text-subheading text-gray-90 max-w-2xl mx-auto">
                        No monthly minimums. Pay as you go– choose a plan that scales with your needs.
                    </p>
                    <p className="text-subheading text-gray-90 max-w-2xl mx-auto mt-1">
                        Integration fees assessed upon consultation.
                    </p>
                </div>

                {/* Individual/Enterprise Toggle */}
                <div className="flex justify-center" style={{ marginTop: '4rem' }}>
                    <div className="relative inline-flex bg-gray-85 rounded-lg" style={{ padding: '0.25rem' }}>
                        {/* Animated slider background */}
                        <div
                            className="absolute bg-gray-95 rounded-md transition-all duration-300 ease-in-out"
                            style={{
                                ...sliderStyle,
                                height: 'calc(100% - 0.5rem)',
                                top: '0.25rem',
                                border: '1px solid transparent',
                                backgroundImage: 'linear-gradient(#f5f5f5, #f5f5f5), linear-gradient(to bottom, #d9d9d9, #b3b3b3)',
                                backgroundOrigin: 'border-box',
                                backgroundClip: 'padding-box, border-box',
                            }}
                        />
                        <button
                            ref={individualRef}
                            onClick={() => setPricingType('individual')}
                            className="relative z-10 text-paragraph font-medium transition-colors duration-300"
                            style={{
                                padding: '0.25rem 0.75rem',
                                color: pricingType === 'individual' ? '#1a1a1a' : '#808080'
                            }}
                        >
                            Individual
                        </button>
                        <button
                            ref={enterpriseRef}
                            onClick={() => setPricingType('enterprise')}
                            className="relative z-10 text-paragraph font-medium transition-colors duration-300"
                            style={{
                                padding: '0.25rem 0.75rem',
                                color: pricingType === 'enterprise' ? '#1a1a1a' : '#808080'
                            }}
                        >
                            Enterprise
                        </button>
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className={`grid grid-cols-1 ${pricingType === 'enterprise' ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-8 max-w-7xl mx-auto`} style={{ marginTop: '1.5rem', gap: '2rem' }}>
                    {tierKeys.map((tierKey, index) => renderPricingCard(tierKey, index))}
                </div>
            </div>
        </div>
    );
};

export default PricingPage;
